import {
  X,
  Edit,
  AlertTriangle,
  LockOpen,
  LockIcon,
  Star,
  Plus,
} from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Carousel } from "../../../../components/ui/carousel";
import {
  type AuditSession,
  type SourceRow,
} from "../../../../models/audit/auditSession";
import { ProcedureSessionTableShell } from "../../../../models/audit/procedureSessionTableShell";
import { extractTimestampFromCitationString } from "../../../../models/audit/citation";
import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { checkResultSchema } from "../../../../models/audit/auditSession";
import { attributeTestResultToCheckResult } from "../../../../models/audit/attributeTestTranslator";
import { z } from "zod";
import FileViewer, {
  BoundingBox,
  type ViewerBoundingBox,
} from "../../../extraction/viewExtraction/FileViewer.tsx";
import { useAudit } from "../../../../contexts/auditContext.tsx";
import { useHookCache } from "../../../../lib/hookCache";
import { useControl } from "../../../../hooks/audit/useControl";
import { LoadingSpinner } from "../../../../components/ui/loadingSpinner/loadingSpinner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../../components/ui/accordion.tsx";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../../../components/ui/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../../components/ui/tabs";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "../../../../components/ui/resizable";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../../components/ui/dialog";
import { Textarea } from "../../../../components/ui/textarea";
import { Label } from "../../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../components/ui/tooltip";
import { CheckResultCard, DocumentMapEntry } from "./CheckResultCard";
import { fetchFastApi } from "../../../../api/fastapi";
import { toast } from "sonner";
import { useAuth } from "../../../../contexts/authContext.tsx";
import { LinkingData } from "../../../extraction/viewExtraction/PdfViewer.tsx";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getColumnDisplayName } from "../../../../lib/columnNameUtils.ts";
import { useColumnActionLogs } from "../../../../hooks/useColumnActionLogs.ts";
import { formatRelativeTime } from "../../../../lib/utils.ts";
import { useHideSourceDocCitations } from "../../../../hooks/useHideSourceDocCitations.ts";
import {
  filterSourceDocCitationsFromSession,
  hasScreenshotCitations,
} from "../../../../lib/citationFilters.ts";
import { Switch } from "../../../../components/ui/switch.tsx";
import {
  AddCitationModal,
  type UploadCitationResponse,
} from "./AddCitationModal";
import { EditCitationSummaryModal } from "./EditCitationSummaryModal";
import { Citation } from "../../../../models/audit/citation";

// Local helpers and subcomponents
const formatIssue = (slug?: string) => {
  if (!slug) return "";
  return slug
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

type EvidenceSummaryProps = {
  auditSession: AuditSession;
  citationDocuments: DocumentMapEntry[];
  activeEvidenceIndex: number;
  exceptions: AuditSession["sourceRows"][number]["checkResults"][number]["issues"];
  sourceRow: AuditSession["sourceRows"][number] | undefined;
  check: AuditSession["procedure"]["columns"][number] | undefined;
  checkResult: AuditSession["sourceRows"][number]["checkResults"][number];
  onEditCitation?: (
    citation: AuditSession["sourceRows"][number]["checkResults"][number]["citations"][number],
  ) => void;
};

const EvidenceSummarySection = ({
  citationDocuments,
  activeEvidenceIndex,
  exceptions,
  sourceRow,
  check,
  checkResult,
  onEditCitation,
}: EvidenceSummaryProps) => {
  const active = citationDocuments[activeEvidenceIndex];
  return (
    <>
      <div className="px-4 mb-2">
        <div>
          <div className="text-sm text-font-secondary mt-1">
            {sourceRow?.sampleNumber
              ? `Sample ${sourceRow.sampleNumber}`
              : "Sample"}{" "}
          </div>
        </div>
        <div className="font-medium">
          {check?.evaluatedColumn?.name || check?.attributeTestColumn?.name}
        </div>
      </div>
      {active && (
        <div className="flex flex-col p-4 border border-blue-200 bg-blue-50">
          <div className="text-sm text-font-secondary font-mono flex items-center justify-between">
            <div className="flex items-center gap-2">
              Evidence Summary
              {active.citations[0]?.isKey === true && (
                <span className="flex items-center gap-1 text-amber-600 text-xs font-normal">
                  <Star className="h-3 w-3 fill-amber-400" />
                  Key evidence
                </span>
              )}
            </div>
            {onEditCitation && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-stone-500 hover:text-stone-700"
                onClick={() => onEditCitation(active.citations[0])}
              >
                <Edit className="h-3 w-3 mr-1" />
                Edit
              </Button>
            )}
          </div>

          <div className="mt-2">{active.citations[0].shortSummary}</div>
        </div>
      )}

      <Accordion type="single" collapsible className="w-full mt-2">
        <AccordionItem value="test-result">
          <AccordionTrigger className="px-4 py-2">Test Result</AccordionTrigger>
          <AccordionContent>
            <div className="p-4 space-y-4">
              <div>
                <div className="text-sm text-font-secondary font-mono">
                  Issues
                </div>
                {exceptions.length > 0 ? (
                  <div className="mt-1 space-y-2">
                    {exceptions.map((ex, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <AlertTriangle className="w-4 h-4 text-amber-700" />
                        <span>{formatIssue(ex.issueSubcategory)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-1 text-sm text-font-secondary">None</div>
                )}
              </div>

              <div>
                <div className="text-sm text-font-secondary font-mono">
                  Conclusion
                </div>
                <div className="mt-1 markdown-body">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {checkResult.reason}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </>
  );
};

type EvidencePagerProps = {
  citationDocuments: DocumentMapEntry[];
  activeIndex: number;
  onSelect: (index: number) => void;
};

const EvidencePager = ({
  citationDocuments,
  activeIndex,
  onSelect,
}: EvidencePagerProps) => (
  <div className="border-t p-2 border-r bg-white sticky bottom-0">
    <div className="text-sm text-font-secondary font-mono mb-2">
      All Evidence
    </div>
    <Carousel
      items={citationDocuments.map((doc, i) => ({
        id: i,
        content:
          doc.citations[0]?.isKey === true ? (
            <span className="flex items-center gap-1">
              <Star className="h-2.5 w-2.5 fill-amber-400 text-amber-400" />
              {i + 1}
            </span>
          ) : (
            i + 1
          ),
      }))}
      selectedIndex={activeIndex}
      onItemSelect={onSelect}
    />
  </div>
);

interface CheckAnalysisPanelProps {
  auditSession: AuditSession;
  checkId?: string;
  checkResultId?: string;
  sourceRowId?: string;
  evalColumnId?: string;
  onClose: () => void;
  handleCheckSelection: ({
    sourceRowId,
    documentId,
    checkResultId,
    supportColumnId,
    value,
  }: {
    sourceRowId?: string;
    attributeId?: string;
    documentId?: string;
    checkResultId?: string;
    supportColumnId?: string;
    value?: string;
  }) => void;
}

export const CheckAnalysisV2 = ({
  auditSession,
  checkResultId,
  sourceRowId,
  onClose,
  handleCheckSelection,
}: CheckAnalysisPanelProps) => {
  // Phase 3: optimistic per-procedure writes still target the heavy
  // hydrated cache entry under `['procedure-session', auditSession.id]`.
  // Where applicable, sites that mutate a single source row now also
  // touch `['procedure-session', psId, 'source-rows', srId]` so lazy
  // `useSourceRow` consumers see the optimistic update.
  const { controlSession } = useAudit();
  const { setEntry } = useHookCache();
  const { user } = useAuth();
  const isSessionComplete = controlSession?.status === "complete";
  // Phase 3 bridge: `useControl` stays at this call site for
  // `hydratedControl.attributes` / `keyReportItems`, which are NOT on the
  // control-session envelope or the per-procedure table-shell. Per
  // `instructions.md`: leave with a clear comment until BE Phase 3+
  // extends the envelope `control` field.
  const { data: hydratedControl } = useControl(controlSession?.controlId);
  const attributes =
    controlSession?.workStep?.attributes ?? hydratedControl?.attributes;
  const { logs: activityLogs, loading: logsLoading } = useColumnActionLogs({
    checkResultId,
    fetchOnLoad: true,
  });
  const [hideSourceDocCitations, setHideSourceDocCitations] =
    useHideSourceDocCitations();

  // Add state for the selected document
  const [currentSelectionValue, setCurrentSelectionValue] = useState<
    string | undefined
  >(undefined);

  const [currentBoundingBox, setCurrentBoundingBox] = useState<
    | {
        boundingBox?: BoundingBox;
        pageNumber?: number;
        pageWidth?: number;
        pageHeight?: number;
        elementId?: string;
        citationId?: string;
        timestampSeconds?: number;
      }
    | undefined
  >(undefined);

  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [showNewConclusionModal, setShowNewConclusionModal] = useState(false);
  const [showAddCitationModal, setShowAddCitationModal] = useState(false);
  const [showEditCitationModal, setShowEditCitationModal] = useState(false);
  const [citationToEdit, setCitationToEdit] = useState<Citation | null>(null);
  const [newConclusion, setNewConclusion] = useState("");
  const [selectedException, setSelectedException] = useState<string | null>(
    null,
  );
  const [isSubmittingNewConclusion, setIsSubmittingNewConclusion] =
    useState(false);
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [activeEvidenceIndex, setActiveEvidenceIndex] = useState<number>(0);
  const tabsListRef = useRef<HTMLDivElement | null>(null);
  const walkthroughContainerRef = useRef<HTMLDivElement | null>(null);
  const prevShowNewConclusionModalRef = useRef(false);

  const {
    updateCurrentDocument,
    selectedDocument,
    currentSelectedDocumentUrl,
  } = useAudit();

  const sourceRow = useMemo(() => {
    return auditSession.sourceRows.find((row) => row.id === sourceRowId);
  }, [auditSession.sourceRows, sourceRowId]);

  const checkResult = useMemo(() => {
    // First, try to find in regular checkResults
    const regularResult = sourceRow?.checkResults.find(
      (result) => result.id === checkResultId,
    );
    if (regularResult) return regularResult;

    // If not found and the ID looks like an attribute test result, try to reconstruct it
    if (checkResultId?.startsWith("attr-test-") && sourceRow) {
      // New format: attr-test-id-{resultId} - lookup by actual result ID
      if (checkResultId.startsWith("attr-test-id-")) {
        const resultId = checkResultId.substring("attr-test-id-".length);
        const attributeTestResult = sourceRow.attributeTestResults?.find(
          (r) => r.id === resultId,
        );

        if (attributeTestResult) {
          // Find the column for this result
          const column = auditSession.procedure.columns.find(
            (c) =>
              c.attributeTestColumn?.id ===
              attributeTestResult.attributeTestColumnId,
          );

          if (column) {
            return attributeTestResultToCheckResult(
              attributeTestResult,
              column,
              sourceRow.id,
            );
          }
        }
      }

      // Legacy format: attr-test-{sourceRowId}-{procedureColumnId}-{reviewOrder}
      // Both sourceRowId and procedureColumnId are UUIDs with dashes, so we need to extract properly
      const prefix = `attr-test-${sourceRow.id}-`;
      const remainder = checkResultId.substring(prefix.length);

      // The remainder is {procedureColumnId}-{reviewOrder}
      // We need to find the last dash to separate columnId from reviewOrder
      const lastDashIndex = remainder.lastIndexOf("-");
      const procedureColumnId = remainder.substring(0, lastDashIndex);
      const reviewOrder = parseInt(remainder.substring(lastDashIndex + 1), 10);

      // Find the column
      const column = auditSession.procedure.columns.find(
        (c) => c.id === procedureColumnId && c.attributeTestColumn,
      );

      if (column?.attributeTestColumn) {
        // Find the attribute test result by matching both attributeTestColumnId and reviewOrder
        const attributeTestResult = sourceRow.attributeTestResults?.find(
          (r) =>
            r.attributeTestColumnId === column.attributeTestColumn!.id &&
            r.reviewOrder === reviewOrder,
        );

        if (attributeTestResult) {
          const translated = attributeTestResultToCheckResult(
            attributeTestResult,
            column,
            sourceRow.id,
          );

          return translated;
        }
      }
    }

    return undefined;
  }, [sourceRow, checkResultId, auditSession.procedure.columns]);

  const check = useMemo(() => {
    return auditSession.procedure.columns.find(
      (c) =>
        c.evaluatedColumn?.id === checkResult?.evalColumnId ||
        c.attributeTestColumn?.id === checkResult?.evalColumnId ||
        c.id === checkResult?.evalColumnId,
    );
  }, [auditSession.procedure.columns, checkResult?.evalColumnId]);

  // Helper to get the column name (works for both evaluated and attribute test columns)
  const columnName = useMemo(() => {
    return check
      ? getColumnDisplayName(
          check,
          auditSession.procedure.columns,
          attributes ?? [],
        )
      : "";
  }, [check, auditSession.procedure.columns, attributes]);

  // Helper to get the column ID (works for both evaluated and attribute test columns)
  const columnId = useMemo(() => {
    return check?.evaluatedColumn?.id || check?.attributeTestColumn?.id;
  }, [check]);

  const hasScreenshots = useMemo(() => {
    if (!checkResult?.citations) return false;
    return hasScreenshotCitations(
      checkResult.citations,
      auditSession.documents,
    );
  }, [checkResult?.citations, auditSession.documents]);

  // kept for possible future use; current summary uses full issues directly
  // const exceptions = useMemo(() => {
  //   return (
  //     checkResult?.issues.filter((issue) => issue.issueCategory !== "test_outcome") || []
  //   );
  // }, [checkResult?.issues]);

  const citationDocuments = useMemo(() => {
    if (!checkResult?.citations || checkResult.citations.length === 0)
      return [];

    // Filter out citations for documents that have screenshot versions
    const filteredCitations = filterSourceDocCitationsFromSession(
      checkResult.citations,
      auditSession.documents,
      hideSourceDocCitations,
    );

    const flat: Array<DocumentMapEntry> = [];
    filteredCitations.forEach((citation) => {
      const sourceDocument = auditSession.documents.find(
        (doc) => doc.documentId === citation.documentId,
      );
      if (sourceDocument) {
        flat.push({ document: sourceDocument, citations: [citation] });
      }
    });
    return flat;
  }, [checkResult?.citations, auditSession.documents, hideSourceDocCitations]);

  const checkResultHistory = useMemo(() => {
    if (!sourceRow) return [];

    let allResults: typeof sourceRow.checkResults = [];

    // Get regular check results
    if (sourceRow.checkResults) {
      allResults = sourceRow.checkResults.filter(
        (result) =>
          result.evalColumnId === columnId && result.id !== checkResultId,
      );
    }

    // Get attribute test results and translate them to CheckResults
    if (check?.attributeTestColumn && sourceRow.attributeTestResults) {
      // Extract result ID from new format (attr-test-id-{resultId}) if applicable
      const currentResultId = checkResultId?.startsWith("attr-test-id-")
        ? checkResultId.substring("attr-test-id-".length)
        : null;

      const attributeTestResults = sourceRow.attributeTestResults
        .filter(
          (r) =>
            r.attributeTestColumnId === check.attributeTestColumn!.id &&
            r.id !== currentResultId,
        )
        .map((r) => attributeTestResultToCheckResult(r, check, sourceRow.id));

      allResults = [...allResults, ...attributeTestResults];
    }

    // Sort by timestamp
    const otherResults = allResults.sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );

    // Find the first human result
    const humanResultIndex = otherResults.findIndex(
      (result) => result.reviewerType === "human",
    );

    // If no human result, include all results
    if (humanResultIndex < 0) {
      return otherResults;
    }

    // Include all results up to and including the first human result
    const resultsUpToFirstHuman = otherResults.slice(0, humanResultIndex + 1);

    // For results after the first human, only include additional human results
    const resultsAfterFirstHuman = otherResults
      .slice(humanResultIndex + 1)
      .filter((result) => result.reviewerType === "human");

    return [...resultsUpToFirstHuman, ...resultsAfterFirstHuman];
  }, [sourceRow, checkResultId, columnId, check]);

  const handleDocumentClick = useCallback(
    (documentEntry: DocumentMapEntry) => {
      const { document, citations } = documentEntry;

      // Use the first citation's information for initial view
      const firstCitation = citations[0];

      // Set the citation-specific information
      setCurrentSelectionValue(firstCitation.shortSummary || undefined);

      // Store page dimensions and raw bounding box for FileViewer
      // Try to get timestamp from top-level field first, then parse from encoded string
      // Note: encodedCitationString may be in either field depending on source
      const encodedString =
        firstCitation.encodedCitationString || firstCitation.documentElement;
      const parsedTimestamp = extractTimestampFromCitationString(
        encodedString || "",
      );
      const timestampSeconds =
        firstCitation.timestampSeconds ?? parsedTimestamp;
      setCurrentBoundingBox({
        boundingBox: firstCitation.boundingBox ?? undefined,
        pageNumber: firstCitation.pageNumber ?? undefined,
        pageWidth: firstCitation.pageWidth ?? undefined,
        pageHeight: firstCitation.pageHeight ?? undefined,
        elementId: firstCitation.documentElement ?? undefined,
        citationId: firstCitation.id ?? undefined,
        timestampSeconds,
      });

      // Update the document and show viewer
      updateCurrentDocument({
        id: document.id,
        documentId: document.documentId,
        fileName: document.name || undefined,
        fileType: document.fetchableDocument?.fileType,
        fetchableDocument: document.fetchableDocument || undefined,
        isMissing: false,
        type: document.fetchableDocument?.fileType,
        procedureSessionId: auditSession.id,
      });
      setShowDocumentViewer(true);
    },
    [
      auditSession.id,
      setCurrentSelectionValue,
      setCurrentBoundingBox,
      updateCurrentDocument,
      setShowDocumentViewer,
    ],
  );

  const openWalkthroughForDocument = useCallback(
    (documentEntry: DocumentMapEntry) => {
      const targetId = documentEntry.citations[0]?.id;
      if (targetId) {
        const idx = citationDocuments.findIndex(
          (e) => e.citations[0]?.id === targetId,
        );
        if (idx >= 0) setActiveEvidenceIndex(idx);
      }
      handleDocumentClick(documentEntry);
      setActiveTab("walkthrough");
    },
    [citationDocuments, handleDocumentClick],
  );

  useEffect(() => {
    setShowDocumentViewer(false);
  }, [checkResultId]);

  useEffect(() => {
    if (activeTab === "walkthrough" && citationDocuments.length > 0) {
      const idx = Math.max(
        0,
        Math.min(activeEvidenceIndex, citationDocuments.length - 1),
      );
      handleDocumentClick(citationDocuments[idx]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activeEvidenceIndex, citationDocuments.length]);

  // When entering the walkthrough tab, remove focus from any tab triggers
  useEffect(() => {
    if (activeTab !== "walkthrough") return;
    const activeEl = document.activeElement as HTMLElement | null;
    if (activeEl && tabsListRef.current?.contains(activeEl)) {
      activeEl.blur();
    }
    tabsListRef.current
      ?.querySelectorAll('[role="tab"]')
      .forEach((el) => (el as HTMLElement).blur());
  }, [activeTab]);

  // Keyboard navigation for Evidence Walkthrough (ArrowLeft/ArrowRight)
  useEffect(() => {
    const isTextInput = (el: Element | null) => {
      if (!el) return false;
      const tag = el.tagName;
      const editable = (el as HTMLElement).isContentEditable;
      return (
        editable || tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT"
      );
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (activeTab !== "walkthrough") return;
      if (isTextInput(e.target as Element)) return;

      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setActiveEvidenceIndex((idx) => Math.max(0, idx - 1));
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        setActiveEvidenceIndex((idx) =>
          Math.min(citationDocuments.length - 1, idx + 1),
        );
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [activeTab, citationDocuments.length]);

  // Prefill form when modal opens (only on transition from closed to open)
  useEffect(() => {
    const wasOpen = prevShowNewConclusionModalRef.current;
    prevShowNewConclusionModalRef.current = showNewConclusionModal;

    // Only prefill when the modal transitions from closed to open
    if (showNewConclusionModal && !wasOpen && checkResult) {
      setNewConclusion(checkResult.reason || "");

      // Find the first exception in the issues array
      setSelectedException(checkResult.issues.length > 0 ? "found" : null);
    }
  }, [showNewConclusionModal, checkResult]);

  // Keyboard shortcut to open chat dialog (press "c")
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Don't trigger if user is typing in an input/textarea or if modifier keys are pressed
      const target = event.target as HTMLElement;
      const isTyping =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;

      if (
        !isTyping &&
        event.key === "c" &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        !event.shiftKey
      ) {
        event.preventDefault();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, []);

  const handleExceptionChange = (exceptionValue: string) => {
    setSelectedException(exceptionValue === "none" ? null : exceptionValue);
  };

  const resetNewConclusionForm = () => {
    setNewConclusion("");
    setSelectedException(null);
    setShowNewConclusionModal(false);
  };

  // Determine if this is an attribute test column (for conditional rendering)
  const isAttributeTestColumn = !!check?.attributeTestColumn;

  // Get the current attribute test result for citation creation
  const currentAttributeTestResult = useMemo(() => {
    if (!isAttributeTestColumn || !sourceRow || !check?.attributeTestColumn) {
      return null;
    }
    return (
      sourceRow.attributeTestResults?.find(
        (r) =>
          r.attributeTestColumnId === check.attributeTestColumn!.id &&
          r.reviewOrder === checkResult?.reviewOrder,
      ) || null
    );
  }, [isAttributeTestColumn, sourceRow, check, checkResult?.reviewOrder]);

  // Get reperformance steps for the Add Citation modal
  const reperformanceSteps = useMemo(() => {
    return currentAttributeTestResult?.reperformanceSteps || [];
  }, [currentAttributeTestResult]);

  const handleNewConclusionSubmit = async () => {
    if (!sourceRowId || !checkResult || !check) return;

    setIsSubmittingNewConclusion(true);

    const isAttributeTestColumn = !!check.attributeTestColumn;

    try {
      if (isAttributeTestColumn) {
        // Handle attribute test column conclusion
        const currentAttributeTestResult =
          sourceRow?.attributeTestResults?.find(
            (r) =>
              r.attributeTestColumnId === check.attributeTestColumn!.id &&
              r.reviewOrder === checkResult.reviewOrder,
          );

        // Ensure we have a current result to create a human review from
        if (!currentAttributeTestResult) {
          toast.error("No attribute test result found to review");
          setIsSubmittingNewConclusion(false);
          return;
        }

        if (!currentAttributeTestResult.id) {
          toast.error(
            "Cannot create human review for this result. This is an older attribute test result that needs to be updated. Please re-run the attribute test to generate a new result with full support for human reviews.",
            { duration: 8000 },
          );
          setIsSubmittingNewConclusion(false);
          return;
        }

        // Determine outcome based on selected exception
        const outcome =
          selectedException === "found"
            ? "fail"
            : selectedException === "not_applicable"
              ? "not_applicable"
              : "pass";

        // Use the new human-review endpoint which handles copying automatically
        const response = await fetchFastApi({
          method: "POST",
          path: `/matching/attribute-test-results/${currentAttributeTestResult.id}/human-review`,
          body: {
            outcome,
            outcome_description: newConclusion,
            outcome_short_description:
              newConclusion.slice(0, 100) +
              (newConclusion.length > 100 ? "..." : ""),
          },
        });

        const newResult = response.result;

        // Phase 3: per-row optimistic write — `['procedure-session', psId,
        // 'source-rows', srId]` is the `useSourceRow` cache entry the
        // workpaper view consumes after Defect 1 removed
        // `useProcedureSession` from the workpaper view.
        if (sourceRowId) {
          setEntry<SourceRow>(
            ["procedure-session", auditSession.id, "source-rows", sourceRowId],
            (prev) => ({
              ...prev,
              attributeTestResults: [
                ...(prev.attributeTestResults || []),
                newResult,
              ],
            }),
          );
        }

        // Switch to the new result
        const newCheckResultId = `attr-test-${sourceRowId}-${check.id}-${newResult.reviewOrder}`;
        handleCheckSelection({
          sourceRowId: sourceRowId,
          documentId: undefined,
          checkResultId: newCheckResultId,
          supportColumnId: undefined,
          value: undefined,
        });
      } else {
        // Handle evaluated column conclusion (existing logic)
        const issues = [];

        // Determine decision based on selected exception
        let decision: "pass" | "fail" | "not_applicable";
        if (selectedException === "not_applicable") {
          decision = "not_applicable";
          issues.push({
            issueCategory: "test_outcome" as const,
            issueSubcategory: "not_applicable" as const,
            description: "Not applicable",
          });
        } else if (selectedException === "found") {
          decision = "fail";
          issues.push({
            issueCategory: "test_outcome" as const,
            issueSubcategory: "issues_found" as const,
            description: "User identified exception",
          });
        } else {
          decision = "pass";
        }

        const old_citations = checkResult.citations || [];

        const newConclusionCheckResult: z.infer<typeof checkResultSchema> = {
          id: crypto.randomUUID(),
          checkId: checkResult.checkId,
          evalColumnId: checkResult.evalColumnId,
          reviewerType: "human" as const,
          userId: user?.id || null,
          decision,
          reason: newConclusion,
          reviewOrder: (checkResult.reviewOrder || 0) + 1,
          issues: issues,
          lockedBy: user?.id || null,
          citations: old_citations,
          columnVersion: checkResult.columnVersion,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        const response = await fetchFastApi({
          method: "POST",
          path: `/matching/match-session/${auditSession.id}/source-row/${sourceRowId}/check-result/`,
          body: {
            check_result: newConclusionCheckResult,
          },
        });

        const responseCheckResult = checkResultSchema.parse(
          response.check_result,
        );

        // Phase 3: per-row optimistic write (see comment on the analogous
        // attribute-test write above).
        if (sourceRowId) {
          setEntry<SourceRow>(
            ["procedure-session", auditSession.id, "source-rows", sourceRowId],
            (prev) => ({
              ...prev,
              checkResults: [...prev.checkResults, responseCheckResult],
            }),
          );
        }

        handleCheckSelection({
          sourceRowId: sourceRowId,
          documentId: undefined,
          checkResultId: responseCheckResult.id,
          supportColumnId: undefined,
          value: undefined,
        });
      }

      toast.success("New conclusion saved successfully");
      resetNewConclusionForm();
    } catch (error) {
      console.error("Error saving new conclusion:", error);
      toast.error("Failed to save new conclusion");
    } finally {
      setIsSubmittingNewConclusion(false);
    }
  };

  const handleLinkingComplete = useCallback(
    async (linkingData: LinkingData) => {
      if (!sourceRowId || !checkResultId || !columnId) {
        toast.error(
          "Missing source row, check result, or eval column information",
        );
        return;
      }

      // Prefer the specific box being edited (dragged). Fallback to current selection.
      const targetCitationId =
        linkingData.editingBoundingBoxId || currentBoundingBox?.citationId;
      const citation = checkResult?.citations.find(
        (c) => c.id === targetCitationId,
      );
      if (!citation) {
        toast.error("Citation not found");
        return;
      }

      // The box and its page dimensions must share a coordinate space. Prefer the
      // dims the box was drawn in (`linkingData`); fall back to the page's
      // extraction dims, then a previously-seen box's dims.
      const pages = currentSelectedDocumentUrl?.documentExtraction?.pages;
      const pageMeta = pages?.[linkingData.pageNumber - 1];
      const pageWidth =
        linkingData.pageWidth ||
        pageMeta?.width ||
        currentBoundingBox?.pageWidth ||
        pages?.[0]?.width;
      const pageHeight =
        linkingData.pageHeight ||
        pageMeta?.height ||
        currentBoundingBox?.pageHeight ||
        pages?.[0]?.height;

      const newCitation = {
        ...citation,
        boundingBox: linkingData.boundingBox,
        pageNumber: linkingData.pageNumber,
        pageWidth: pageWidth,
        pageHeight: pageHeight,
      };

      // Phase 3: per-row optimistic write.
      if (sourceRowId) {
        setEntry<SourceRow>(
          ["procedure-session", auditSession.id, "source-rows", sourceRowId],
          (prev) => {
            // For attribute test columns, update attributeTestResults
            if (check?.attributeTestColumn && citation.reperformanceStepId) {
              return {
                ...prev,
                attributeTestResults: prev.attributeTestResults?.map(
                  (attrResult) =>
                    attrResult.attributeTestColumnId ===
                    check.attributeTestColumn?.id
                      ? {
                          ...attrResult,
                          reperformanceSteps: attrResult.reperformanceSteps.map(
                            (step) =>
                              step.id === citation.reperformanceStepId
                                ? {
                                    ...step,
                                    citations: step.citations.map((c) =>
                                      c.id === citation.id
                                        ? {
                                            ...c,
                                            boundingBox:
                                              newCitation.boundingBox,
                                            pageNumber: newCitation.pageNumber,
                                            pageWidth:
                                              newCitation.pageWidth ?? null,
                                            pageHeight:
                                              newCitation.pageHeight ?? null,
                                          }
                                        : c,
                                    ),
                                  }
                                : step,
                          ),
                        }
                      : attrResult,
                ),
              };
            }

            // For evaluated columns, update checkResults
            return {
              ...prev,
              checkResults: prev.checkResults.map((result) =>
                result.id === checkResultId
                  ? {
                      ...result,
                      citations: result.citations.map((c) =>
                        c.id === citation.id
                          ? {
                              ...c,
                              ...newCitation,
                              pageWidth: newCitation.pageWidth ?? null,
                              pageHeight: newCitation.pageHeight ?? null,
                            }
                          : c,
                      ),
                    }
                  : result,
              ),
            };
          },
        );
      }
      setCurrentBoundingBox({
        boundingBox: linkingData.boundingBox,
        pageNumber: linkingData.pageNumber,
        pageWidth: pageWidth,
        pageHeight: pageHeight,
        elementId: "",
        citationId: citation.id,
      });

      try {
        // Use different API endpoints for attribute test vs evaluated columns
        if (check?.attributeTestColumn) {
          // Attribute test columns use the independent citations API
          if (!citation.reperformanceStepId) {
            toast.error(
              "Missing reperformance step ID for attribute test citation",
            );
            return;
          }

          await fetchFastApi({
            method: "PUT",
            path: `/citations/${citation.id}`,
            body: {
              citation: {
                id: newCitation.id,
                documentId: newCitation.documentId,
                encodedCitationString: newCitation.documentElement,
                shortSummary: newCitation.shortSummary,
                boundingBox: newCitation.boundingBox,
                pageNumber: newCitation.pageNumber,
                pageWidth: newCitation.pageWidth,
                pageHeight: newCitation.pageHeight,
                citationOrder: newCitation.citationOrder,
                reperformanceStepId: newCitation.reperformanceStepId,
                createdAt: newCitation.createdAt,
                updatedAt: newCitation.updatedAt,
              },
              reperformance_step_id: citation.reperformanceStepId,
            },
          });
        } else {
          // Evaluated columns use the existing eval-column API
          await fetchFastApi({
            method: "POST",
            path: `/matching/match-session/${auditSession.id}/source-row/${sourceRowId}/eval-column/${columnId}/citation/update`,
            body: {
              citation: newCitation,
              check_result_id: checkResultId,
            },
          });
        }
      } catch (error) {
        console.error("Error linking citation:", error);
        toast.error("Failed to link citation");
      }
    },
    [
      checkResult,
      check,
      columnId,
      currentBoundingBox,
      auditSession.id,
      sourceRowId,
      currentSelectedDocumentUrl,
      checkResultId,
      setEntry,
    ],
  );

  const handleDeleteCitation = useCallback(
    async ({ id, index }: { id?: string; index: number }) => {
      if (!sourceRowId || !checkResultId) {
        toast.error("Missing source row or check result information");
        return;
      }

      const targetCitationId = id || checkResult?.citations[index]?.id;
      const citation = checkResult?.citations.find(
        (c) => c.id === targetCitationId,
      );

      if (!citation) {
        toast.error("Citation not found");
        return;
      }

      // Phase 3: per-row optimistic write.
      if (sourceRowId) {
        setEntry<SourceRow>(
          ["procedure-session", auditSession.id, "source-rows", sourceRowId],
          (prev) => {
            if (check?.attributeTestColumn && citation.reperformanceStepId) {
              return {
                ...prev,
                attributeTestResults: prev.attributeTestResults?.map(
                  (attrResult) =>
                    attrResult.attributeTestColumnId ===
                    check.attributeTestColumn?.id
                      ? {
                          ...attrResult,
                          inlineCitations: attrResult.inlineCitations.filter(
                            (c) => c.id !== citation.id,
                          ),
                          reperformanceSteps: attrResult.reperformanceSteps.map(
                            (step) => ({
                              ...step,
                              citations: step.citations.filter(
                                (c) => c.id !== citation.id,
                              ),
                            }),
                          ),
                        }
                      : attrResult,
                ),
              };
            }

            return {
              ...prev,
              checkResults: prev.checkResults.map((result) =>
                result.id === checkResultId
                  ? {
                      ...result,
                      citations: result.citations.filter(
                        (c) => c.id !== citation.id,
                      ),
                    }
                  : result,
              ),
            };
          },
        );
      }

      const remainingCitations = (checkResult?.citations || []).filter(
        (c) => c.id !== citation.id,
      );
      if (remainingCitations.length > 0) {
        const newIndex = Math.max(0, index - 1);
        const newCitation = remainingCitations[newIndex];
        if (newCitation) {
          setCurrentBoundingBox({
            boundingBox: newCitation.boundingBox || undefined,
            pageNumber: newCitation.pageNumber || undefined,
            pageWidth: newCitation.pageWidth || undefined,
            pageHeight: newCitation.pageHeight || undefined,
            elementId: newCitation.documentElement || undefined,
            citationId: newCitation.id,
          });
          setActiveEvidenceIndex(newIndex);
        }
      } else {
        setCurrentBoundingBox(undefined);
        setActiveEvidenceIndex(0);
      }

      try {
        if (check?.attributeTestColumn) {
          await fetchFastApi({
            method: "DELETE",
            path: `/citations/${citation.id}`,
          });
        } else {
          await fetchFastApi({
            method: "DELETE",
            path: `/citations/legacy/${citation.id}`,
          });
        }
        toast.success("Citation deleted");
      } catch (error) {
        console.error("Error deleting citation:", error);
        toast.error("Failed to delete citation");
      }
    },
    [
      checkResult,
      check,
      auditSession.id,
      sourceRowId,
      checkResultId,
      setEntry,
      setCurrentBoundingBox,
      setActiveEvidenceIndex,
    ],
  );

  const handleCreateCitation = useCallback(
    async (data: {
      summary: string;
      reperformanceStepId: string | null;
      linkingData: LinkingData;
      documentId: string;
      pageWidth?: number;
      pageHeight?: number;
      isKey?: boolean;
    }) => {
      if (!sourceRowId || !checkResultId || !columnId) {
        throw new Error("Missing source row, check result, or column ID");
      }

      const newCitationId = crypto.randomUUID();
      const now = new Date().toISOString();

      const newCitation = {
        id: newCitationId,
        documentId: data.documentId,
        encodedCitationString: `pdf-manual#${data.documentId}`,
        shortSummary: data.summary,
        boundingBox: data.linkingData.boundingBox,
        pageNumber: data.linkingData.pageNumber,
        pageWidth: data.pageWidth ?? null,
        pageHeight: data.pageHeight ?? null,
        citationOrder: (checkResult?.citations.length || 0) + 1,
        reperformanceStepId: data.reperformanceStepId,
        isKey: data.isKey ?? true,
        createdAt: now,
        updatedAt: now,
      };

      // Build citation objects for local state update
      // checkResultCitationSchema uses documentElement, Citation schema uses encodedCitationString
      const checkResultCitation = {
        id: newCitationId,
        documentId: data.documentId,
        documentElement: `pdf-manual#${data.documentId}`,
        shortSummary: data.summary,
        boundingBox: data.linkingData.boundingBox,
        pageNumber: data.linkingData.pageNumber,
        pageWidth: data.pageWidth ?? null,
        pageHeight: data.pageHeight ?? null,
        citationOrder: newCitation.citationOrder,
        reperformanceStepId: data.reperformanceStepId,
        attributeIds: [],
        reasonSpan: null,
        isKey: data.isKey ?? true,
        createdAt: now,
        updatedAt: now,
      };

      const attributeTestCitation = {
        id: newCitationId,
        documentId: data.documentId,
        encodedCitationString: `pdf-manual#${data.documentId}`,
        shortSummary: data.summary,
        boundingBox: data.linkingData.boundingBox,
        pageNumber: data.linkingData.pageNumber,
        pageWidth: data.pageWidth ?? null,
        pageHeight: data.pageHeight ?? null,
        citationOrder: newCitation.citationOrder,
        reperformanceStepId: data.reperformanceStepId,
        isKey: data.isKey ?? true,
        createdAt: now,
        updatedAt: now,
      };

      // Phase 3: per-row optimistic write.
      if (sourceRowId) {
        setEntry<SourceRow>(
          ["procedure-session", auditSession.id, "source-rows", sourceRowId],
          (prev) => {
            if (isAttributeTestColumn && data.reperformanceStepId) {
              return {
                ...prev,
                attributeTestResults: prev.attributeTestResults?.map(
                  (attrResult) =>
                    attrResult.attributeTestColumnId ===
                    check?.attributeTestColumn?.id
                      ? {
                          ...attrResult,
                          reperformanceSteps: attrResult.reperformanceSteps.map(
                            (step) =>
                              step.id === data.reperformanceStepId
                                ? {
                                    ...step,
                                    citations: [
                                      ...step.citations,
                                      attributeTestCitation,
                                    ],
                                  }
                                : step,
                          ),
                        }
                      : attrResult,
                ),
              };
            }

            return {
              ...prev,
              checkResults: prev.checkResults.map((result) =>
                result.id === checkResultId
                  ? {
                      ...result,
                      citations: [...result.citations, checkResultCitation],
                    }
                  : result,
              ),
            };
          },
        );
      }

      // POST to the citations endpoint
      if (isAttributeTestColumn && data.reperformanceStepId) {
        await fetchFastApi({
          method: "POST",
          path: `/citations`,
          body: {
            citation: {
              id: newCitation.id,
              document_id: newCitation.documentId,
              encoded_citation_string: newCitation.encodedCitationString,
              short_summary: newCitation.shortSummary,
              bounding_box: newCitation.boundingBox,
              page_number: newCitation.pageNumber,
              page_width: newCitation.pageWidth,
              page_height: newCitation.pageHeight,
              citation_order: newCitation.citationOrder,
              is_key: newCitation.isKey,
              reperformance_step_id: data.reperformanceStepId,
              created_at: newCitation.createdAt,
              updated_at: newCitation.updatedAt,
            },
            reperformance_step_id: data.reperformanceStepId,
          },
        });
      } else {
        await fetchFastApi({
          method: "POST",
          path: `/citations`,
          body: {
            citation: {
              id: newCitation.id,
              document_id: newCitation.documentId,
              encoded_citation_string: newCitation.encodedCitationString,
              short_summary: newCitation.shortSummary,
              bounding_box: newCitation.boundingBox,
              page_number: newCitation.pageNumber,
              page_width: newCitation.pageWidth,
              page_height: newCitation.pageHeight,
              citation_order: newCitation.citationOrder,
              is_key: newCitation.isKey,
              created_at: newCitation.createdAt,
              updated_at: newCitation.updatedAt,
            },
            eval_result_id: checkResultId,
          },
        });
      }
    },
    [
      sourceRowId,
      checkResultId,
      columnId,
      checkResult?.citations.length,
      auditSession.id,
      isAttributeTestColumn,
      check?.attributeTestColumn?.id,
      setEntry,
    ],
  );

  // Handle upload citation created - update local state with new document and citation
  const handleUploadCitationCreated = useCallback(
    (response: UploadCitationResponse) => {
      const { citation, document } = response;

      // Build the citation object for local state
      const newCitation = {
        id: citation.id,
        documentId: citation.documentId,
        documentElement: citation.encodedCitationString,
        shortSummary: citation.shortSummary,
        boundingBox: citation.boundingBox,
        pageNumber: citation.pageNumber,
        pageWidth: citation.pageWidth,
        pageHeight: citation.pageHeight,
        citationOrder: citation.citationOrder,
        reperformanceStepId: citation.reperformanceStepId,
        attributeIds: [],
        reasonSpan: null,
        isKey: citation.isKey,
        createdAt: citation.createdAt,
        updatedAt: citation.updatedAt,
      };

      const attributeTestCitation = {
        id: citation.id,
        documentId: citation.documentId,
        encodedCitationString: citation.encodedCitationString,
        shortSummary: citation.shortSummary,
        boundingBox: citation.boundingBox,
        pageNumber: citation.pageNumber,
        pageWidth: citation.pageWidth,
        pageHeight: citation.pageHeight,
        citationOrder: citation.citationOrder,
        reperformanceStepId: citation.reperformanceStepId,
        isKey: citation.isKey,
        createdAt: citation.createdAt,
        updatedAt: citation.updatedAt,
      };

      // Build the document object for local state
      // Use sessionDocument.id for the session-document ID (used by fetchPresignedUrl lookup)
      // Fall back to document.id for backwards compatibility
      const newDocument = {
        id: response.sessionDocument?.id ?? document.id,
        documentId: document.id,
        name: document.fileName,
        fetchableDocument: {
          id: document.id,
          fileName: document.fileName,
          fileType: document.fileType,
          fileUrl: document.fileUrl,
          fileContentHash: document.fileContentHash,
          documentAssignments: null,
          extra: document.extra,
          parentDocumentId: document.parentDocumentId,
          childType: null,
          state: "ready" as const,
          isParsed: document.isParsed,
          createdAt: document.createdAt,
          updatedAt: document.updatedAt,
        },
        creatorType: "user" as const,
        state: "approved" as const,
        extraction: null,
        extractions: [],
        // A file uploaded as a citation is not an analytics input until the
        // auditor marks it as one.
        isAnalyticsInput: false,
      };

      // Phase 3: optimistic per-row write for the citation; the new
      // document is added to the table-shell cache so the synthesised
      // session in `ProcedureSessionWorkPaper` picks it up.
      setEntry<ProcedureSessionTableShell>(
        ["procedure-session", auditSession.id, "table-shell"],
        (prev) => ({
          ...prev,
          documents: [...prev.documents, { ...newDocument, sampleNumbers: [] }],
        }),
      );
      if (sourceRowId) {
        setEntry<SourceRow>(
          ["procedure-session", auditSession.id, "source-rows", sourceRowId],
          (prev) => {
            if (isAttributeTestColumn && citation.reperformanceStepId) {
              return {
                ...prev,
                attributeTestResults: prev.attributeTestResults?.map(
                  (attrResult) =>
                    attrResult.id === check?.attributeTestColumn?.id
                      ? {
                          ...attrResult,
                          reperformanceSteps: attrResult.reperformanceSteps.map(
                            (step) =>
                              step.id === citation.reperformanceStepId
                                ? {
                                    ...step,
                                    citations: [
                                      ...step.citations,
                                      attributeTestCitation,
                                    ],
                                  }
                                : step,
                          ),
                        }
                      : attrResult,
                ),
              };
            }

            return {
              ...prev,
              checkResults: prev.checkResults.map((result) =>
                result.id === checkResultId
                  ? {
                      ...result,
                      citations: [...result.citations, newCitation],
                    }
                  : result,
              ),
            };
          },
        );
      }
    },
    [
      auditSession.id,
      sourceRowId,
      checkResultId,
      isAttributeTestColumn,
      check?.attributeTestColumn?.id,
      setEntry,
    ],
  );

  // Build bounding boxes for the currently selected document to show all citations
  const currentDocId = useMemo(() => {
    const active = citationDocuments[activeEvidenceIndex];
    return active?.document?.documentId;
  }, [citationDocuments, activeEvidenceIndex]);

  const viewerBoundingBoxes: ViewerBoundingBox[] | undefined = useMemo(() => {
    if (!currentDocId) return undefined;

    // Filter out citations for documents that have screenshot versions
    const filteredCitations = filterSourceDocCitationsFromSession(
      checkResult?.citations || [],
      auditSession.documents,
      hideSourceDocCitations,
    );

    const boxes = filteredCitations
      .filter((c) => c.documentId === currentDocId && !!c.boundingBox)
      .map((c) => ({
        id: c.id || undefined,
        boundingBox: c.boundingBox!,
        pageNumber: c.pageNumber || undefined,
        pageWidth: c.pageWidth || undefined,
        pageHeight: c.pageHeight || undefined,
      }));
    return boxes.length > 0 ? boxes : undefined;
  }, [
    checkResult?.citations,
    currentDocId,
    auditSession.documents,
    hideSourceDocCitations,
  ]);

  const selectedBoundingBoxId: string | undefined = useMemo(() => {
    if (!currentBoundingBox?.citationId) return undefined;
    return currentBoundingBox.citationId;
  }, [currentBoundingBox?.citationId]);

  const selectedBoundingBoxIndex: number | undefined = useMemo(() => {
    if (!viewerBoundingBoxes || !currentBoundingBox?.citationId)
      return undefined;
    const idx = viewerBoundingBoxes.findIndex(
      (b) => b.id === currentBoundingBox.citationId,
    );
    return idx >= 0 ? idx : undefined;
  }, [viewerBoundingBoxes, currentBoundingBox?.citationId]);

  const handleLockCheckResult = useCallback(async () => {
    try {
      // Phase 3: per-row optimistic write — toggles `checkResult.lockedBy`
      // on the row's checkResults.
      if (sourceRowId) {
        setEntry<SourceRow>(
          ["procedure-session", auditSession.id, "source-rows", sourceRowId],
          (prev) => ({
            ...prev,
            checkResults: prev.checkResults.map((result) =>
              result.id === checkResult?.id
                ? {
                    ...result,
                    lockedBy: checkResult?.lockedBy ? null : user?.id || null,
                  }
                : result,
            ),
          }),
        );
      }
      await fetchFastApi({
        method: "PUT",
        path: `/matching/match-session/${auditSession.id}/source-row/${sourceRowId}/check-result/${checkResult?.id}/lock`,
        body: {
          locked_by: checkResult?.lockedBy ? null : user?.id || null,
        },
      });
    } catch (error) {
      console.error("Error locking check result:", error);
      toast.error("Failed to lock check result");
    }
  }, [user?.id, auditSession.id, checkResult, sourceRowId, setEntry]);

  const handleToggleKeyCitation = useCallback(async () => {
    if (!sourceRowId || !checkResultId || !columnId) {
      toast.error("Missing required information");
      return;
    }

    const targetCitationId = currentBoundingBox?.citationId;
    const citation = checkResult?.citations.find(
      (c) => c.id === targetCitationId,
    );

    if (!citation) {
      toast.error("No citation selected");
      return;
    }

    const newIsKeyValue = !citation.isKey;

    // Phase 3: per-row optimistic write.
    if (sourceRowId) {
      setEntry<SourceRow>(
        ["procedure-session", auditSession.id, "source-rows", sourceRowId],
        (prev) => {
          if (check?.attributeTestColumn && citation.reperformanceStepId) {
            return {
              ...prev,
              attributeTestResults: prev.attributeTestResults?.map(
                (attrResult) =>
                  attrResult.attributeTestColumnId ===
                  check.attributeTestColumn?.id
                    ? {
                        ...attrResult,
                        reperformanceSteps: attrResult.reperformanceSteps.map(
                          (step) =>
                            step.id === citation.reperformanceStepId
                              ? {
                                  ...step,
                                  citations: step.citations.map((c) =>
                                    c.id === citation.id
                                      ? { ...c, isKey: newIsKeyValue }
                                      : c,
                                  ),
                                }
                              : step,
                        ),
                      }
                    : attrResult,
              ),
            };
          }

          return {
            ...prev,
            checkResults: prev.checkResults.map((result) =>
              result.id === checkResultId
                ? {
                    ...result,
                    citations: result.citations.map((c) =>
                      c.id === citation.id ? { ...c, isKey: newIsKeyValue } : c,
                    ),
                  }
                : result,
            ),
          };
        },
      );
    }

    try {
      // Use different API endpoints for attribute test vs evaluated columns
      if (check?.attributeTestColumn) {
        // Attribute test columns use the independent citations API
        if (!citation.reperformanceStepId) {
          toast.error(
            "Missing reperformance step ID for attribute test citation",
          );
          return;
        }

        await fetchFastApi({
          method: "PUT",
          path: `/citations/${citation.id}`,
          body: {
            citation: {
              id: citation.id,
              documentId: citation.documentId,
              encodedCitationString: citation.documentElement,
              shortSummary: citation.shortSummary,
              boundingBox: citation.boundingBox,
              pageNumber: citation.pageNumber,
              pageWidth: citation.pageWidth,
              pageHeight: citation.pageHeight,
              citationOrder: citation.citationOrder,
              reperformanceStepId: citation.reperformanceStepId,
              isKey: newIsKeyValue,
              createdAt: citation.createdAt,
              updatedAt: citation.updatedAt,
            },
            reperformance_step_id: citation.reperformanceStepId,
          },
        });
      } else {
        // Evaluated columns use the existing eval-column API
        await fetchFastApi({
          method: "POST",
          path: `/matching/match-session/${auditSession.id}/source-row/${sourceRowId}/eval-column/${columnId}/citation/update`,
          body: {
            citation: {
              ...citation,
              isKey: newIsKeyValue,
            },
            check_result_id: checkResultId,
          },
        });
      }

      toast.success(
        newIsKeyValue
          ? "Marked as key evidence"
          : "Removed key evidence marker",
      );
    } catch (error) {
      console.error("Error toggling key citation:", error);
      toast.error("Failed to update citation");
    }
  }, [
    checkResult,
    check,
    columnId,
    currentBoundingBox,
    auditSession.id,
    sourceRowId,
    checkResultId,
    setEntry,
  ]);

  const handleEditCitationSummary = useCallback(
    (citation: z.infer<typeof checkResultSchema>["citations"][number]) => {
      // Convert to Citation type for the modal
      const citationForModal: Citation = {
        id: citation.id,
        documentId: citation.documentId,
        encodedCitationString: citation.documentElement,
        shortSummary: citation.shortSummary,
        boundingBox: citation.boundingBox,
        pageNumber: citation.pageNumber,
        pageWidth: citation.pageWidth,
        pageHeight: citation.pageHeight,
        citationOrder: citation.citationOrder ?? null,
        reperformanceStepId: citation.reperformanceStepId ?? null,
        createdAt:
          typeof citation.createdAt === "string"
            ? citation.createdAt
            : citation.createdAt.toISOString(),
        updatedAt:
          typeof citation.updatedAt === "string"
            ? citation.updatedAt
            : citation.updatedAt.toISOString(),
      };
      setCitationToEdit(citationForModal);
      setShowEditCitationModal(true);
    },
    [],
  );

  const handleUpdateCitationSummary = useCallback(
    async (citationId: string, newSummary: string) => {
      if (!sourceRowId || !checkResultId || !columnId) {
        toast.error("Missing required information");
        return;
      }

      const citation = checkResult?.citations.find((c) => c.id === citationId);
      if (!citation) {
        toast.error("Citation not found");
        return;
      }

      // Phase 3: per-row optimistic write.
      if (sourceRowId) {
        setEntry<SourceRow>(
          ["procedure-session", auditSession.id, "source-rows", sourceRowId],
          (prev) => {
            if (check?.attributeTestColumn && citation.reperformanceStepId) {
              return {
                ...prev,
                attributeTestResults: prev.attributeTestResults?.map(
                  (attrResult) =>
                    attrResult.attributeTestColumnId ===
                    check.attributeTestColumn?.id
                      ? {
                          ...attrResult,
                          reperformanceSteps: attrResult.reperformanceSteps.map(
                            (step) =>
                              step.id === citation.reperformanceStepId
                                ? {
                                    ...step,
                                    citations: step.citations.map((c) =>
                                      c.id === citation.id
                                        ? { ...c, shortSummary: newSummary }
                                        : c,
                                    ),
                                  }
                                : step,
                          ),
                        }
                      : attrResult,
                ),
              };
            }

            return {
              ...prev,
              checkResults: prev.checkResults.map((result) =>
                result.id === checkResultId
                  ? {
                      ...result,
                      citations: result.citations.map((c) =>
                        c.id === citation.id
                          ? { ...c, shortSummary: newSummary }
                          : c,
                      ),
                    }
                  : result,
              ),
            };
          },
        );
      }

      try {
        // Use different API endpoints for attribute test vs evaluated columns
        if (check?.attributeTestColumn) {
          await fetchFastApi({
            method: "PUT",
            path: `/citations/${citation.id}`,
            body: {
              citation: {
                id: citation.id,
                documentId: citation.documentId,
                encodedCitationString: citation.documentElement,
                shortSummary: newSummary,
                boundingBox: citation.boundingBox,
                pageNumber: citation.pageNumber,
                pageWidth: citation.pageWidth,
                pageHeight: citation.pageHeight,
                citationOrder: citation.citationOrder,
                reperformanceStepId: citation.reperformanceStepId,
                isKey: citation.isKey,
                createdAt: citation.createdAt,
                updatedAt: citation.updatedAt,
              },
              reperformance_step_id: citation.reperformanceStepId ?? null,
            },
          });
        } else {
          await fetchFastApi({
            method: "POST",
            path: `/matching/match-session/${auditSession.id}/source-row/${sourceRowId}/eval-column/${columnId}/citation/update`,
            body: {
              citation: {
                ...citation,
                shortSummary: newSummary,
              },
              check_result_id: checkResultId,
            },
          });
        }

        toast.success("Citation summary updated");
      } catch (error) {
        console.error("Error updating citation summary:", error);
        toast.error("Failed to update citation summary");
      }
    },
    [
      checkResult,
      check,
      columnId,
      auditSession.id,
      sourceRowId,
      checkResultId,
      setEntry,
    ],
  );

  if (!sourceRow || !checkResult || !check) {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="text-xl font-semibold">Check Details</div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <p>No check details found.</p>
      </div>
    );
  }

  return (
    <Sheet open={true} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        hideClose
        className={`w-full p-0  ${
          activeTab === "walkthrough"
            ? "sm:w-[85vw] sm:max-w-none"
            : "sm:max-w-[720px]"
        }`}
      >
        <div className="h-screen flex flex-row overflow-hidden">
          <div className="flex-1 flex flex-col min-w-0">
            <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
              <div className="flex items-center gap-3">
                <img src="/logo_orange_bg.svg" alt="logo" className="w-6" />
                <div className="flex flex-col">
                  <SheetHeader>
                    <SheetTitle>Midship Walkthrough</SheetTitle>
                  </SheetHeader>
                  <div className="text-xs text-font-secondary">
                    {(sourceRow?.sampleNumber
                      ? `Sample ${sourceRow.sampleNumber}`
                      : "Sample") + (columnName ? ` · ${columnName}` : "")}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!isSessionComplete && (
                  <Button
                    variant="secondary"
                    onClick={() => setShowNewConclusionModal(true)}
                    className="flex items-center gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    New Conclusion
                  </Button>
                )}
                <Button variant="ghost" size="icon" onClick={onClose}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            <div className="px-6 py-3">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <div className="flex items-center justify-between gap-2">
                  <TabsList ref={tabsListRef}>
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="walkthrough">
                      Evidence Walkthrough
                    </TabsTrigger>
                  </TabsList>
                  {hasScreenshots && (
                    <div className="flex items-center gap-2">
                      <label
                        htmlFor="screenshot-toggle"
                        className="text-sm text-stone-600 cursor-pointer"
                      >
                        Show screenshots only
                      </label>
                      <Switch
                        id="screenshot-toggle"
                        checked={hideSourceDocCitations}
                        onCheckedChange={setHideSourceDocCitations}
                      />
                    </div>
                  )}
                </div>

                <TabsContent value="overview">
                  <div className="p-2 h-[calc(100vh-160px)] overflow-y-auto">
                    <CheckResultCard
                      checkResult={checkResult}
                      columnName={columnName}
                      check={check}
                      sourceRowId={sourceRowId}
                      auditSession={auditSession}
                      onDocumentClick={openWalkthroughForDocument}
                      showInstructions={true}
                      showAuthor={true}
                      hideSourceDocCitations={hideSourceDocCitations}
                    />

                    {checkResultHistory && checkResultHistory.length > 0 && (
                      <div className="mt-4">
                        <Accordion type="single" collapsible>
                          <AccordionItem value="history" className="border-0">
                            <AccordionTrigger className="text-sm font-semibold">
                              History
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="space-y-4">
                                {checkResultHistory
                                  .sort((a, b) => {
                                    return (
                                      new Date(b.createdAt).getTime() -
                                      new Date(a.createdAt).getTime()
                                    );
                                  })
                                  .map((historyResult) => (
                                    <CheckResultCard
                                      key={historyResult.id}
                                      checkResult={historyResult}
                                      columnName={columnName}
                                      check={check}
                                      sourceRowId={sourceRowId}
                                      auditSession={auditSession}
                                      onDocumentClick={
                                        openWalkthroughForDocument
                                      }
                                      showInstructions={false}
                                      showAuthor={true}
                                      hideSourceDocCitations={
                                        hideSourceDocCitations
                                      }
                                    />
                                  ))}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      </div>
                    )}

                    {activityLogs.length > 0 && (
                      <div className="mt-4">
                        <Accordion type="single" collapsible>
                          <AccordionItem value="activity" className="border-0">
                            <AccordionTrigger className="text-sm font-semibold">
                              Activity
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="space-y-3">
                                {logsLoading ? (
                                  <div className="flex justify-center py-4">
                                    <LoadingSpinner />
                                  </div>
                                ) : (
                                  activityLogs.map((log, index) => (
                                    <div
                                      key={log.id}
                                      className="flex gap-3 text-xs text-font-secondary relative"
                                    >
                                      {index < activityLogs.length - 1 && (
                                        <div className="absolute left-[11px] top-6 bottom-0 w-px bg-stone-200" />
                                      )}
                                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-stone-200 flex items-center justify-center text-[10px] font-medium text-stone-600 relative z-10">
                                        {log.user?.email
                                          ?.charAt(0)
                                          .toUpperCase() || "?"}
                                      </div>
                                      <div className="flex-1 min-w-0 pt-0.5">
                                        <div className="flex items-baseline gap-2 flex-wrap">
                                          <span className="font-medium text-stone-900">
                                            {log.user?.email || "Unknown user"}
                                          </span>
                                          <span className="text-stone-500">
                                            {formatRelativeTime(log.createdAt)}
                                          </span>
                                        </div>
                                        <div className="mt-1 text-stone-600">
                                          {log.log}
                                        </div>
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="walkthrough">
                  <div
                    ref={walkthroughContainerRef}
                    tabIndex={-1}
                    className="h-[calc(100vh-160px)] overflow-hidden focus:outline-none focus-visible:outline-none"
                  >
                    <ResizablePanelGroup
                      direction="horizontal"
                      className="h-full"
                    >
                      {/* Left summary panel */}
                      <ResizablePanel
                        defaultSize={35}
                        minSize={25}
                        maxSize={60}
                      >
                        <div className="h-full flex flex-col min-h-0">
                          <div className="p-2 flex-1 min-h-0 overflow-y-auto">
                            <EvidenceSummarySection
                              auditSession={auditSession}
                              citationDocuments={citationDocuments}
                              activeEvidenceIndex={activeEvidenceIndex}
                              exceptions={checkResult.issues}
                              sourceRow={sourceRow}
                              check={check}
                              checkResult={checkResult}
                              onEditCitation={
                                isSessionComplete
                                  ? undefined
                                  : handleEditCitationSummary
                              }
                            />
                          </div>
                          {citationDocuments.length > 1 && (
                            <EvidencePager
                              citationDocuments={citationDocuments}
                              activeIndex={activeEvidenceIndex}
                              onSelect={(i) => {
                                const next = Math.max(
                                  0,
                                  Math.min(i, citationDocuments.length - 1),
                                );
                                setActiveEvidenceIndex(next);
                                handleDocumentClick(citationDocuments[next]);
                              }}
                            />
                          )}
                        </div>
                      </ResizablePanel>

                      <ResizableHandle withHandle />

                      {/* Right viewer panel */}
                      <ResizablePanel defaultSize={65} minSize={40}>
                        <div className="h-full overflow-x-auto min-h-0 flex flex-col">
                          {showDocumentViewer && selectedDocument ? (
                            currentSelectedDocumentUrl ? (
                              <div className="h-full w-full flex flex-col min-h-0">
                                <div className="flex items-center justify-between gap-2 ml-4 mb-1">
                                  <div className="w-fit px-4 py-2 text-sm bg-stone-50 border border-stone-200 truncate shrink-0">
                                    {selectedDocument?.fileName ||
                                      selectedDocument?.fetchableDocument
                                        ?.fileName ||
                                      ""}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {!isSessionComplete && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                          setShowAddCitationModal(true)
                                        }
                                        className="flex items-center gap-2"
                                      >
                                        <Plus className="h-4 w-4" />
                                        Add Citation
                                      </Button>
                                    )}
                                    {currentBoundingBox?.citationId &&
                                      !isSessionComplete && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={handleToggleKeyCitation}
                                          className="flex items-center gap-2"
                                        >
                                          <Star
                                            className={`h-4 w-4 ${
                                              checkResult?.citations.find(
                                                (c) =>
                                                  c.id ===
                                                  currentBoundingBox.citationId,
                                              )?.isKey
                                                ? "fill-amber-400 text-amber-400"
                                                : ""
                                            }`}
                                          />
                                          Mark as Key Citation
                                        </Button>
                                      )}
                                  </div>
                                </div>
                                <div className="flex-1 min-h-0 overflow-auto">
                                  <FileViewer
                                    allowLinking={!isSessionComplete}
                                    allowDeleting={!isSessionComplete}
                                    signedUrl={currentSelectedDocumentUrl?.url}
                                    fileType={
                                      selectedDocument?.fetchableDocument
                                        ?.fileType || "pdf"
                                    }
                                    fileName={
                                      selectedDocument?.fileName ||
                                      selectedDocument?.fetchableDocument
                                        ?.fileName
                                    }
                                    initialPage={currentBoundingBox?.pageNumber}
                                    boundingBoxes={viewerBoundingBoxes}
                                    boundingBox={
                                      currentBoundingBox?.boundingBox
                                    }
                                    pageWidth={currentBoundingBox?.pageWidth}
                                    pageHeight={currentBoundingBox?.pageHeight}
                                    elementId={currentBoundingBox?.elementId}
                                    selectedBoundingBoxId={
                                      selectedBoundingBoxId
                                    }
                                    selectedBoundingBoxIndex={
                                      selectedBoundingBoxIndex
                                    }
                                    defaultSearchValue={currentSelectionValue}
                                    documentExtraction={
                                      currentSelectedDocumentUrl?.documentExtraction
                                    }
                                    videoDocument={
                                      currentSelectedDocumentUrl?.videoDocument
                                    }
                                    initialTimestamp={
                                      currentBoundingBox?.timestampSeconds
                                    }
                                    onLinkingComplete={handleLinkingComplete}
                                    onDelete={handleDeleteCitation}
                                    onSelect={({ id }) => {
                                      if (!id) return;
                                      const clicked = (
                                        checkResult?.citations || []
                                      ).find((c) => c.id === id);
                                      if (!clicked) return;
                                      // Immediately sync current selection to the clicked citation
                                      const clickedEncodedString =
                                        clicked.encodedCitationString ||
                                        clicked.documentElement;
                                      const parsedTs =
                                        extractTimestampFromCitationString(
                                          clickedEncodedString || "",
                                        );
                                      setCurrentBoundingBox({
                                        boundingBox:
                                          clicked.boundingBox || undefined,
                                        pageNumber:
                                          clicked.pageNumber || undefined,
                                        pageWidth:
                                          clicked.pageWidth || undefined,
                                        pageHeight:
                                          clicked.pageHeight || undefined,
                                        elementId:
                                          clicked.documentElement || undefined,
                                        citationId: clicked.id,
                                        timestampSeconds:
                                          clicked.timestampSeconds ?? parsedTs,
                                      });
                                      setCurrentSelectionValue(
                                        clicked.shortSummary || undefined,
                                      );
                                      setActiveEvidenceIndex(
                                        (
                                          checkResult?.citations || []
                                        ).findIndex((c) => c.id === id),
                                      );
                                    }}
                                  />
                                </div>
                              </div>
                            ) : (
                              <div className="h-full flex items-center justify-center">
                                <LoadingSpinner />
                              </div>
                            )
                          ) : (
                            <div className="h-full flex items-center justify-center text-sm text-font-secondary">
                              Select an evidence item to preview.
                            </div>
                          )}
                        </div>
                      </ResizablePanel>
                    </ResizablePanelGroup>
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            {/* New Conclusion Modal */}
            <Dialog
              open={showNewConclusionModal}
              onOpenChange={setShowNewConclusionModal}
            >
              <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                  <DialogTitle>New Conclusion</DialogTitle>
                </DialogHeader>
                <div className="grid gap-6 py-4">
                  <div className="grid gap-2">
                    <Label>Outcome</Label>

                    <Select
                      onValueChange={handleExceptionChange}
                      value={selectedException || "none"}
                    >
                      <SelectTrigger className="max-w-md">
                        <SelectValue placeholder="Select an exception" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Issues found</SelectItem>
                        <SelectItem value="found">Issues found</SelectItem>
                        {isAttributeTestColumn && (
                          <SelectItem value="not_applicable">N/A</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <div className="text-sm text-font-secondary">
                      How the results will appear
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="reason">
                      Conclusion <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="reason"
                      placeholder="Enter your conclusion for this test result..."
                      value={newConclusion}
                      onChange={(e) => setNewConclusion(e.target.value)}
                      rows={4}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={resetNewConclusionForm}
                    disabled={isSubmittingNewConclusion}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleNewConclusionSubmit}
                    disabled={
                      !newConclusion.trim() || isSubmittingNewConclusion
                    }
                  >
                    {isSubmittingNewConclusion ? "Submitting..." : "Save"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Add Citation Modal */}
            <AddCitationModal
              open={showAddCitationModal}
              onOpenChange={setShowAddCitationModal}
              auditSession={auditSession}
              isAttributeTestColumn={isAttributeTestColumn}
              reperformanceSteps={reperformanceSteps}
              evalResultId={checkResult?.id}
              onCreateCitation={handleCreateCitation}
              onUploadCitationCreated={handleUploadCitationCreated}
            />

            {/* Edit Citation Summary Modal */}
            <EditCitationSummaryModal
              open={showEditCitationModal}
              onOpenChange={setShowEditCitationModal}
              citation={citationToEdit}
              onSave={handleUpdateCitationSummary}
            />
          </div>

          {activeTab === "overview" && !isSessionComplete && (
            <div className="border-l border-gray-200 flex items-center justify-center p-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleLockCheckResult}
                      className={`${checkResult?.lockedBy ? "text-white bg-green-700 hover:bg-green-800 hover:text-white" : "text-green-700 bg-gray-100 hover:bg-gray-200 hover:text-green-700"}`}
                    >
                      {checkResult?.lockedBy ? (
                        <LockOpen className="h-4 w-4" />
                      ) : (
                        <LockIcon className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      {checkResult?.lockedBy ? "Unlock Answer" : "Lock Answer"}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
